import { invoke } from '@tauri-apps/api/core';
import { isTauri } from '../utils/isTauri';
import { androidLocalCheck, isAndroidOverlay } from '../utils/androidBridge';
import { isAndroid } from '../utils/environment';

/**
 * Local, offline grammar/spelling checking — the fast layer that runs before (and mostly
 * instead of) an LLM call. Backed by Harper in `src-tauri/src/grammar.rs`: no network,
 * no per-call cost, typically 1-6ms.
 *
 * Desktop only. On web and in the Android overlay the backend command does not exist, so
 * every function here degrades to "no issues" instead of throwing. Note this is *not* the
 * `isAndroidOverlay`-before-`isTauri()` situation described in CLAUDE.md: there is no
 * Android code path to skip here, because the checker is deliberately not shipped in the
 * APK (the FST dictionary would add ~14MB).
 */

/** Harper's category for an issue. Handy for colouring underlines or muting a category. */
export type GrammarIssueKind =
  | 'Agreement'
  | 'BoundaryError'
  | 'Capitalization'
  | 'Eggcorn'
  | 'Enhancement'
  | 'Formatting'
  | 'Grammar'
  | 'Malapropism'
  | 'Miscellaneous'
  | 'Nonstandard'
  | 'Punctuation'
  | 'Readability'
  | 'Redundancy'
  | 'Regionalism'
  | 'Repetition'
  | 'Spelling'
  | 'Style'
  | 'Typo'
  | 'Usage'
  | 'WordChoice'
  | 'WordOrder';

export interface GrammarIssue {
  /** JS string index of the first character of the problem. */
  start: number;
  /** JS string index just past the last character of the problem. */
  end: number;
  /** The offending text itself. */
  text: string;
  kind: GrammarIssueKind;
  /** Human-readable explanation, safe to show in a tooltip. */
  message: string;
  /** Replacements for `[start, end)`, best first. Use `applyGrammarSuggestion` to apply one. */
  suggestions: string[];
  /** Lower = more important. */
  priority: number;
}

/** `US` | `GB` | `CA` | `AU` | `IN`, or a BCP-47 tag such as `en-GB`. */
export type GrammarDialect = 'US' | 'GB' | 'CA' | 'AU' | 'IN';

export interface GrammarOptions {
  dialect?: GrammarDialect | string;
  /**
   * Return nothing when the text doesn't look like English. Off by default, because the
   * detector rejects short fragments containing an unknown word — exactly the
   * "I has a error" case we most want to catch. Prefer gating on the language you already
   * know from the UI over turning this on.
   */
  skipNonEnglish?: boolean;
}

/**
 * Whether the local checker can be reached from here.
 *
 * `isAndroidOverlay` is checked FIRST, per the rule in CLAUDE.md: the correction popup on
 * Android is a plain WebView, so `isTauri()` is false there even though the checker is
 * very much available — over the JNI bridge instead of invoke().
 */
export const isGrammarCheckerAvailable = (): boolean => isAndroidOverlay || isTauri();

/**
 * Check `text` and return the issues found, ordered by position.
 * Resolves to `[]` — never rejects — when the checker isn't available, so callers can
 * treat "no backend" and "no problems" the same way.
 */
export const checkGrammar = async (
  text: string,
  options: GrammarOptions = {}
): Promise<GrammarIssue[]> => {
  if (!isGrammarCheckerAvailable() || !text.trim()) {
    return [];
  }

  try {
    if (isAndroidOverlay) {
      return await androidLocalCheck<GrammarIssue[]>({
        cmd: 'checkGrammar',
        text,
        dialect: options.dialect ?? null,
        skip_non_english: options.skipNonEnglish ?? null,
      });
    }

    return await invoke<GrammarIssue[]>('check_grammar', {
      text,
      options: {
        dialect: options.dialect ?? null,
        // Rust reads this as `skip_non_english`; see GrammarOptions in grammar.rs.
        skip_non_english: options.skipNonEnglish ?? null,
      },
    });
  } catch (error) {
    console.error('Local grammar check failed:', error);
    return [];
  }
};

/**
 * Ask the backend whether the text looks like English, based on the share of words the
 * English dictionary recognises.
 *
 * `null` means "too short to tell" (fewer than four words) — treat that as unknown, not as
 * "no", or short sentences would never get checked. Note `checkGrammar` already applies
 * this gate itself when `skipNonEnglish` is set; this is for UI that wants to explain why
 * nothing is being underlined.
 */
export const isTextLikelyEnglish = async (text: string): Promise<boolean | null> => {
  if (!isGrammarCheckerAvailable() || !text.trim()) {
    return null;
  }

  try {
    return await invoke<boolean | null>('is_text_likely_english', { text });
  } catch (error) {
    console.error('Language detection failed:', error);
    return null;
  }
};

/** Dialects the local checker supports, for a settings dropdown. */
export const getGrammarDialects = async (): Promise<GrammarDialect[]> => {
  if (!isGrammarCheckerAvailable()) {
    return [];
  }

  try {
    return await invoke<GrammarDialect[]>('get_grammar_dialects');
  } catch (error) {
    console.error('Failed to load grammar dialects:', error);
    return [];
  }
};

/** Apply one suggestion to `text`. The backend normalises suggestions so this is a splice. */
export const applyGrammarSuggestion = (
  text: string,
  issue: GrammarIssue,
  suggestion: string
): string => text.slice(0, issue.start) + suggestion + text.slice(issue.end);

/**
 * Apply the first suggestion of every issue in one pass ("fix all").
 * Works back to front so earlier fixes don't shift the offsets of later ones, and skips
 * issues Harper couldn't suggest a fix for.
 */
export const applyAllGrammarSuggestions = (text: string, issues: GrammarIssue[]): string =>
  [...issues]
    .filter((issue) => issue.suggestions.length > 0)
    .sort((a, b) => b.start - a.start)
    .reduce((acc, issue) => applyGrammarSuggestion(acc, issue, issue.suggestions[0]), text);

/**
 * The issue list as it should look immediately after applying `suggestion` to `issue`.
 *
 * A re-check is debounced by a few hundred milliseconds, and in that window the old
 * offsets no longer describe the new text — later issues would underline whatever moved
 * into their span. So we drop the fixed issue and slide everything after it by the length
 * delta. The next real check overwrites this; it only has to be right in the meantime.
 */
export const shiftIssuesAfterFix = (
  issues: GrammarIssue[],
  issue: GrammarIssue,
  suggestion: string
): GrammarIssue[] => {
  const delta = suggestion.length - (issue.end - issue.start);

  return issues
    .filter((candidate) => candidate !== issue)
    .map((candidate) =>
      candidate.start >= issue.end
        ? { ...candidate, start: candidate.start + delta, end: candidate.end + delta }
        : candidate
    )
    // An issue that overlapped the one we just fixed describes text that no longer exists.
    .filter((candidate) => candidate.start < issue.start || candidate.start >= issue.end);
};

// ---------------------------------------------------------------------------
// Spelling (non-English languages)
// ---------------------------------------------------------------------------

/**
 * Harper covers English — grammar and spelling — but nothing else. The other languages
 * the app ships in are spell-checked against Hunspell dictionaries via `spelling.rs`.
 * Those dictionaries are downloaded on demand (~1-7MB each), not bundled.
 *
 * Turkish is missing on purpose: every Hunspell Turkish dictionary we could find declares
 * a flag value Hunspell's own format doesn't allow, and no parser will take it. See the
 * CATALOG comment in spelling.rs.
 */
export const SPELLING_LANGUAGES = ['nl', 'ar', 'de', 'fr', 'es', 'it', 'ru'] as const;
export type SpellingLanguage = (typeof SPELLING_LANGUAGES)[number];

/** Languages the local check can handle at all — English through Harper, the rest here. */
export type WritingLanguage = 'en' | SpellingLanguage;

export const isSpellingLanguage = (code: string): code is SpellingLanguage =>
  (SPELLING_LANGUAGES as readonly string[]).includes(code);

/** True for any language the local check can do something with. */
export const isSupportedWritingLanguage = (code: string): code is WritingLanguage =>
  code === 'en' || isSpellingLanguage(code);

/**
 * Endonyms — each language written in its own script. Deliberately not i18n keys: a
 * language picker reads best this way, and it saves nine translations per language.
 */
export const WRITING_LANGUAGE_LABELS: Record<WritingLanguage, string> = {
  en: 'English',
  nl: 'Nederlands',
  ar: '\u0627\u0644\u0639\u0631\u0628\u064a\u0629',
  de: 'Deutsch',
  fr: 'Fran\u00e7ais',
  es: 'Espa\u00f1ol',
  it: 'Italiano',
  ru: '\u0420\u0443\u0441\u0441\u043a\u0438\u0439',
};

export interface SpellingLanguageInfo {
  code: SpellingLanguage;
  installed: boolean;
  approx_bytes: number;
  license: string;
}

export const getSpellingLanguages = async (): Promise<SpellingLanguageInfo[]> => {
  if (!isGrammarCheckerAvailable()) return [];

  try {
    if (isAndroidOverlay) {
      return await androidLocalCheck<SpellingLanguageInfo[]>({ cmd: 'listLanguages' });
    }

    return await invoke<SpellingLanguageInfo[]>('get_spelling_languages');
  } catch (error) {
    console.error('Failed to list spelling dictionaries:', error);
    return [];
  }
};

/** Rejects with a message the UI can show — the caller needs to know a download failed. */
export const downloadSpellingDictionary = (language: SpellingLanguage): Promise<void> =>
  isAndroidOverlay
    ? androidLocalCheck<void>({ cmd: 'downloadDictionary', language })
    : invoke('download_spelling_dictionary', { language });

export const deleteSpellingDictionary = (language: SpellingLanguage): Promise<void> =>
  invoke('delete_spelling_dictionary', { language });

export interface SpellingResult {
  issues: GrammarIssue[];
  /** The language is supported but its dictionary hasn't been downloaded yet. */
  dictionaryMissing: boolean;
}

/**
 * Spell-check text in a non-English language.
 *
 * The returned issues carry **no suggestions**: producing them costs 10-90ms per word in
 * the backend, so underlines appear first and `suggestSpelling` fills the fix buttons in
 * afterwards for the handful of issues actually on screen.
 */
export const checkSpelling = async (
  text: string,
  language: SpellingLanguage
): Promise<SpellingResult> => {
  if (!isGrammarCheckerAvailable() || !text.trim()) {
    return { issues: [], dictionaryMissing: false };
  }

  try {
    const issues = isAndroidOverlay
      ? await androidLocalCheck<GrammarIssue[]>({ cmd: 'checkSpelling', text, language })
      : await invoke<GrammarIssue[]>('check_spelling', { text, language });

    return { issues, dictionaryMissing: false };
  } catch (error) {
    const message = String(error);
    if (message.includes('not installed')) {
      return { issues: [], dictionaryMissing: true };
    }
    console.error('Local spell check failed:', error);
    return { issues: [], dictionaryMissing: false };
  }
};

export const suggestSpelling = async (
  word: string,
  language: SpellingLanguage,
  limit = 3
): Promise<string[]> => {
  if (!isGrammarCheckerAvailable()) return [];

  try {
    if (isAndroidOverlay) {
      return await androidLocalCheck<string[]>({ cmd: 'suggestSpelling', word, language, limit });
    }

    return await invoke<string[]>('suggest_spelling', { word, language, limit });
  } catch (error) {
    console.error('Spelling suggestions failed:', error);
    return [];
  }
};

/** Teach the checker a word so it stops being flagged. */
export const addWordToDictionary = (word: string, language: SpellingLanguage): Promise<void> =>
  isAndroidOverlay
    ? androidLocalCheck<void>({ cmd: 'addWord', word, language })
    : invoke('add_word_to_dictionary', { word, language });

export const getUserDictionary = async (language: SpellingLanguage): Promise<string[]> => {
  if (!isGrammarCheckerAvailable()) return [];

  try {
    return await invoke<string[]>('get_user_dictionary', { language });
  } catch (error) {
    console.error('Failed to read the user dictionary:', error);
    return [];
  }
};

export const removeWordFromDictionary = (
  word: string,
  language: SpellingLanguage
): Promise<void> => invoke('remove_word_from_dictionary', { word, language });

/**
 * Whether the writing button - the one that parks itself beside the field you are typing in
 * - can work here at all.
 *
 * False on Windows and Linux, where the field detection has no implementation yet, and on
 * Android, where the command does not exist and `invoke` rejects. Both end up here, so the
 * settings switch can hide rather than offer a toggle that quietly does nothing.
 */
export const isFieldButtonSupported = async (): Promise<boolean> => {
  if (!isTauri()) return false;

  // Android finds the field through its accessibility service rather than through the
  // desktop command, so it answers here instead of over there.
  if (isAndroid()) return true;

  try {
    return await invoke<boolean>('is_field_button_supported');
  } catch {
    return false;
  }
};

/**
 * Read and write the writing button's on/off state.
 *
 * Two different homes on purpose: desktop keeps it in a Rust atomic, Android in
 * SharedPreferences, because the Android accessibility service has to read it with no
 * webview running at all. Callers shouldn't have to care which.
 */
export const getFieldButtonEnabled = async (): Promise<boolean> => {
  if (!isTauri()) return false;

  try {
    return await invoke<boolean>(
      isAndroid() ? 'mobile_get_writing_button_enabled' : 'get_field_button_enabled'
    );
  } catch (error) {
    console.error('Failed to read the writing button setting:', error);
    return false;
  }
};

export const setFieldButtonEnabled = (enabled: boolean): Promise<void> => {
  // Android keeps this in SharedPreferences and so remembers it by itself. Desktop keeps it
  // in a Rust atomic that starts fresh on every launch, so without a copy here the switch
  // would quietly turn itself back on at the next start - the same shape of problem the
  // clipboard hotkey has, and solved the same way: stored on this side, pushed back at
  // startup (see restoreFieldButtonSetting).
  if (!isAndroid()) setGrammarPreferences({ fieldButtonEnabled: enabled });

  return invoke(isAndroid() ? 'mobile_set_writing_button_enabled' : 'set_field_button_enabled', {
    enabled,
  });
};

/**
 * Push the stored writing-button choice into the backend at startup. Called once from
 * App.tsx; a no-op on Android, which never lost the setting in the first place.
 */
export const restoreFieldButtonSetting = async (): Promise<void> => {
  if (!isTauri() || isAndroid()) return;
  if (!(await isFieldButtonSupported())) return;

  const enabled = getGrammarPreferences().fieldButtonEnabled;
  try {
    await invoke('set_field_button_enabled', { enabled });
  } catch (error) {
    console.error('Failed to restore the writing button setting:', error);
  }
};

// ---------------------------------------------------------------------------
// Preferences
// ---------------------------------------------------------------------------

const PREFS_KEY = 'kalimat-grammar-prefs';

export interface GrammarPreferences {
  /** Master switch for the local checker. */
  enabled: boolean;
  /**
   * The language being written, which decides whether Harper or a Hunspell dictionary
   * runs. `'auto'` follows the app's UI language — the best guess available without
   * loading every dictionary just to detect one.
   */
  writingLanguage: 'auto' | WritingLanguage;
  /** Which English the checker should hold the text to. */
  dialect: GrammarDialect;
  /**
   * Skip text that doesn't look like English. Off by default, and the one switch on the
   * General screen that is: Harper's own detector rejects short fragments containing an
   * unknown word, which is exactly the "I has a error" case worth catching.
   */
  skipNonEnglish: boolean;
  /**
   * The button beside the field you are typing in. Desktop's own copy lives in a Rust atomic
   * that does not survive a restart, so the answer is kept here too - see
   * setFieldButtonEnabled. Android reads its own SharedPreferences and ignores this.
   */
  fieldButtonEnabled: boolean;
  /**
   * Whether the issues panel is rolled up to just its header.
   *
   * Collapsed by DEFAULT: the underlines already say what is wrong and where, so the list
   * is the optional part — it opens when someone actually wants to fix something from it.
   * A preference rather than component state so the choice survives remounts and carries
   * across windows; the underlines are unaffected either way.
   */
  panelCollapsed: boolean;
}

export const DEFAULT_GRAMMAR_PREFERENCES: GrammarPreferences = {
  enabled: true,
  writingLanguage: 'auto',
  dialect: 'US',
  // Off by default, deliberately, and the one switch on the General screen that is: Harper's
  // own language detector rejects short fragments containing an unknown word, which is
  // exactly the "I has a error" case worth catching. Turning it on trades those away.
  skipNonEnglish: false,
  panelCollapsed: true,
  fieldButtonEnabled: false,
};

/**
 * The language the user picked by hand, or null when they left it on `'auto'` (in which
 * case the text itself decides — see `detectWritingLanguage`).
 *
 * This used to resolve `'auto'` against the app's UI language, which was wrong: someone
 * running the app in Dutch and writing English got their English checked as Dutch.
 */
export const explicitWritingLanguage = (
  preferences: GrammarPreferences
): WritingLanguage | null =>
  preferences.writingLanguage !== 'auto' && isSupportedWritingLanguage(preferences.writingLanguage)
    ? preferences.writingLanguage
    : null;

/**
 * Identify the language of the text itself.
 *
 * `null` means "don't act on this": either there are fewer than four words, where trigram
 * detection is a coin flip, or it's a language the local check can do nothing with
 * (Turkish is recognised precisely so it can be declined). Callers should stay quiet on
 * null rather than fall back to a guess — a confident underline in the wrong language is
 * worse than no underline.
 */
export const detectWritingLanguage = async (text: string): Promise<WritingLanguage | null> => {
  if (!isGrammarCheckerAvailable() || !text.trim()) return null;

  try {
    const code = isAndroidOverlay
      ? await androidLocalCheck<string | null>({ cmd: 'detectLanguage', text })
      : await invoke<string | null>('detect_writing_language', { text });

    return code && isSupportedWritingLanguage(code) ? code : null;
  } catch (error) {
    console.error('Language detection failed:', error);
    return null;
  }
};

/**
 * Read the stored preferences. Kept in localStorage rather than the settings API for the
 * same reason the UI language is (`kalimat-language`): it's a purely local, per-device
 * display concern that must be readable synchronously on first render.
 */
export const getGrammarPreferences = (): GrammarPreferences => {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return DEFAULT_GRAMMAR_PREFERENCES;
    // Spread over the defaults so a partial or older stored object stays valid.
    return { ...DEFAULT_GRAMMAR_PREFERENCES, ...JSON.parse(raw) };
  } catch {
    return DEFAULT_GRAMMAR_PREFERENCES;
  }
};

/** Persist preferences and notify any mounted checker so it re-runs immediately. */
export const setGrammarPreferences = (next: Partial<GrammarPreferences>): GrammarPreferences => {
  const merged = { ...getGrammarPreferences(), ...next };

  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(merged));
  } catch (error) {
    console.error('Failed to persist grammar preferences:', error);
  }

  window.dispatchEvent(new CustomEvent(GRAMMAR_PREFS_EVENT, { detail: merged }));
  return merged;
};

/** Fired on `window` whenever preferences change; `useGrammarCheck` listens for it. */
export const GRAMMAR_PREFS_EVENT = 'kalimat-grammar-prefs-changed';
